import {
  resolveNotificationHref as resolveHref,
  type NotificationRouteMap,
} from '@gokrew/ui/utility/notifications';
import {
  NotificationAudience,
  NotificationSubjectType,
  NotificationType,
  type NotificationItem,
} from '@gokrew/models/notifications';
import { EmployeeSection } from '@gokrew/models/employee-change-requests';
import type { EmployeeFlowType } from '@gokrew/models/employee-boarding';
import { DETAILS_REQUESTS_VIEW_PARAM } from '@gokrew/ui/hooks';

/**
 * Employee-detail tab each change-request section lives on.
 * Keyed by every section, so a new one fails the build until it has a page.
 */
const CHANGE_REQUEST_TAB: Record<EmployeeSection, string> = {
  [EmployeeSection.Document]: 'documents',
  [EmployeeSection.Education]: 'education',
  [EmployeeSection.Family]: 'family',
  [EmployeeSection.BankAccount]: 'bank',
  [EmployeeSection.PersonalInformation]: 'info',
};

const isFlowTab = (value: unknown): value is EmployeeFlowType =>
  value === 'onboarding' || value === 'offboarding';

/**
 * Where a manager opens a task. The admin task page serves company tasks only;
 * a flow task is managed on its employee's onboarding or offboarding tab, and
 * one whose context does not say which stays non-clickable.
 */
const taskManagementHref = (taskId: string, item: NotificationItem) => {
  const context = item.context;
  if (context?.source !== 'flow') return `/tasks/${taskId}`;
  const { flowEmployeeId, flowType } = context;
  return typeof flowEmployeeId === 'string' && isFlowTab(flowType)
    ? `/employees/detail/${flowEmployeeId}/${flowType}`
    : null;
};

/** An assignee's copy opens their own tasks, not the task's admin page. */
const taskDetailHref = (item: NotificationItem) => {
  const taskId = item.context?.taskId;
  return item.audience !== NotificationAudience.SUBJECT &&
    typeof taskId === 'string'
    ? taskManagementHref(taskId, item)
    : '/my-tasks';
};

/** HR's notices about one participant of a review cycle. */
const HR_PARTICIPANT_TYPES = new Set<NotificationType>([
  NotificationType.PERFORMANCE_REVIEW_ACKNOWLEDGED,
  NotificationType.PERFORMANCE_REVIEW_SHARED,
  NotificationType.PERFORMANCE_PROBATION_EVALUATIONS,
  NotificationType.PERFORMANCE_PROBATION_EVALUATIONS_LATE,
]);

/** HR's probation digests: the automatic evaluations sent, the late ones. */
const PROBATION_DIGEST_TYPES = new Set<NotificationType>([
  NotificationType.PERFORMANCE_PROBATION_EVALUATIONS,
  NotificationType.PERFORMANCE_PROBATION_EVALUATIONS_LATE,
]);

/** A probation digest that lists several evaluations. */
const isProbationDigestOfMany = (item: NotificationItem) =>
  PROBATION_DIGEST_TYPES.has(item.type) &&
  typeof item.context?.count === 'number' &&
  item.context.count > 1;

/**
 * The automatic review a digest is about (its `programId`, set when every
 * line comes from one), on the late ones for the late digest; a digest
 * across several opens the Reviews list. Older digests carry no program and
 * open `/performance/probation`, which redirects to the end-of-probation one.
 */
const probationListHref = (item: NotificationItem) => {
  const late =
    item.type === NotificationType.PERFORMANCE_PROBATION_EVALUATIONS_LATE;
  const programId = item.context?.programId;
  if (typeof programId === 'string') {
    return `/performance/programs/${encodeURIComponent(programId)}${
      late ? '?status=late' : ''
    }`;
  }
  if (item.context && 'programId' in item.context) {
    return '/performance/reviews';
  }
  return late ? '/performance/probation?status=late' : '/performance/probation';
};

/**
 * Company-portal route per subject type. The backend deliberately sends no
 * URLs, so this map is the portal's own. A subject type missing from it — a
 * newly shipped one, or a resource this portal has no screen for — renders as
 * a non-clickable item.
 */
const SUBJECT_ROUTE: NotificationRouteMap = {
  [NotificationSubjectType.MESSAGING_CONVERSATION]: (id) =>
    `/messages?conversation=${encodeURIComponent(id)}`,
  // A "Needs attention" ping opens the request it is about.
  [NotificationSubjectType.LEAVE_REQUEST]: (id, item) =>
    item.type === NotificationType.ATTENTION_PING
      ? `/leave-management/overview?request=${encodeURIComponent(id)}`
      : '/leave-management/overview',
  [NotificationSubjectType.RECRUITING_JOB]: (id) =>
    `/ats?tab=pipeline&job=${encodeURIComponent(id)}`,
  // "Connect your calendar": the Calendar tab, where Outlook is connected.
  [NotificationSubjectType.CALENDAR_CONNECTION]: () => '/ats?tab=calendar',
  // A colleague's opinion on a shared candidate: the candidate's profile.
  [NotificationSubjectType.RECRUITING_APPLICATION]: (id) =>
    `/ats/applicants/${encodeURIComponent(id)}`,
  [NotificationSubjectType.LEAVE_POLICY]: (id) =>
    `/leave-management/policies/${id}`,
  [NotificationSubjectType.LEAVE_WALLET]: () =>
    '/leave-management/employee-wallets',
  // A direct grant's subject is the grant, not a request — it belongs on the
  // balances screen, not in the requests queue.
  [NotificationSubjectType.LEAVE_GRANT]: () =>
    '/leave-management/employee-wallets',
  [NotificationSubjectType.LEAVE_MANAGER]: () =>
    '/employee_settings/approval-settings',
  [NotificationSubjectType.LEAVE_SCHEDULED_JOB]: () => '/leave-grant-history',
  [NotificationSubjectType.HOLIDAY]: () => '/leave-management/holidays',
  [NotificationSubjectType.HOLIDAY_CALENDAR]: () =>
    '/leave-management/holidays',
  [NotificationSubjectType.TASK]: (id, item) =>
    item.audience === NotificationAudience.SUBJECT
      ? '/my-tasks'
      : taskManagementHref(id, item),
  [NotificationSubjectType.TASK_OCCURRENCE]: (_id, item) =>
    taskDetailHref(item),
  [NotificationSubjectType.TASK_ASSIGNMENT]: (_id, item) =>
    taskDetailHref(item),
  [NotificationSubjectType.SURVEY]: (id) => `/survey/${id}/overview`,
  // The subject is the submission; the manager's review screen is keyed by
  // the review it belongs to, which only the context carries.
  [NotificationSubjectType.PERFORMANCE_REVIEW_SUBMISSION]: (_id, item) => {
    const reviewId = item.context?.reviewId;
    return typeof reviewId === 'string'
      ? `/performance/my-reviews/${reviewId}`
      : null;
  },
  // HR notices open that participant of the cycle: an acknowledgment for the
  // HR user who launched it (A7), and for an automatic probation evaluation
  // its share, the sweep's digest and the late digest (a digest of several
  // opens the probation evaluations list). Anything else opens the
  // admin-manager's own review.
  [NotificationSubjectType.PERFORMANCE_REVIEW]: (id, item) => {
    if (isProbationDigestOfMany(item)) return probationListHref(item);
    return HR_PARTICIPANT_TYPES.has(item.type) &&
      typeof item.context?.cycleId === 'string'
      ? `/performance/reviews/${item.context.cycleId}/participants/${id}`
      : `/performance/my-reviews/${id}`;
  },
  [NotificationSubjectType.PERFORMANCE_GOAL]: () => '/performance/goals',
  [NotificationSubjectType.ONE_ON_ONE]: (id, item) => {
    const meetingId = item.context?.meetingId;
    return typeof meetingId === 'string'
      ? `/performance/one-on-ones/${id}?meeting=${meetingId}`
      : `/performance/one-on-ones/${id}`;
  },
  [NotificationSubjectType.ANNOUNCEMENT]: () => '/notes/announcements',
  [NotificationSubjectType.TIMESHEET_REQUEST]: () => '/timesheet/requests',
  [NotificationSubjectType.TIMESHEET_WEEK]: () => '/timesheet',
  // A manager's probation recommendation is decided from Needs attention; a
  // probation evaluation not sent (no manager) opens the Performance tab,
  // where Start probation review lives.
  [NotificationSubjectType.EMPLOYEE]: (id, item) => {
    if (item.type === NotificationType.PROBATION_RECOMMENDATION) {
      return '/dashboard';
    }
    if (item.type === NotificationType.PERFORMANCE_PROBATION_EVALUATIONS) {
      return isProbationDigestOfMany(item)
        ? probationListHref(item)
        : `/employees/detail/${id}/performance`;
    }
    return `/employees/detail/${id}/info`;
  },
  // The subject id is the termination request, not the employee — the profile
  // has to come off the context, and stays non-clickable without it.
  [NotificationSubjectType.EMPLOYEE_TERMINATION]: (_id, item) => {
    const employeeId = item.context?.employeeId;
    return typeof employeeId === 'string'
      ? `/employees/detail/${employeeId}/info`
      : null;
  },
  // The subject is the request; it is reviewed on the employee's own section
  // tab, which only the context names — opened straight on its Requests view.
  [NotificationSubjectType.EMPLOYEE_CHANGE_REQUEST]: (_id, item) => {
    const employeeId = item.context?.employeeId;
    const tab = CHANGE_REQUEST_TAB[item.context?.section as EmployeeSection];
    return typeof employeeId === 'string' && tab
      ? `/employees/detail/${employeeId}/${tab}?${DETAILS_REQUESTS_VIEW_PARAM}=requests`
      : null;
  },
  [NotificationSubjectType.EMPLOYEE_FLOW]: () => '/tasks/onboarding',
  [NotificationSubjectType.EMPLOYEE_NOTE]: () => '/notes/employee',
  [NotificationSubjectType.HR_NOTE]: () => '/notes/admin',
  [NotificationSubjectType.EMPLOYEE_DOCUMENT]: () => '/files/all',
  // A requested document arrived: the employee's Documents tab, Requests
  // view, on that request. The employee comes off the context.
  [NotificationSubjectType.DOCUMENT_REQUEST]: (id, item) => {
    const employeeId = item.context?.employeeId;
    return typeof employeeId === 'string'
      ? `/employees/detail/${employeeId}/documents?${DETAILS_REQUESTS_VIEW_PARAM}=requests&request=${encodeURIComponent(id)}`
      : null;
  },
  // Approval asked, changes asked, declined, signed, expired, or the company
  // signatory's turn: that request's page.
  [NotificationSubjectType.SIGNATURE_REQUEST]: (id) =>
    `/esignatures/requests/${encodeURIComponent(id)}`,
  // Someone failed a training: that training (the Results tab arrives with
  // PR 4). The subject is the assignment; the training comes off the context.
  [NotificationSubjectType.TRAINING]: (_id, item) => {
    const trainingId = item.context?.trainingId;
    return typeof trainingId === 'string'
      ? `/training/${encodeURIComponent(trainingId)}`
      : null;
  },
  // An external training to review: the External trainings tab.
  [NotificationSubjectType.TRAINING_EXTERNAL]: () => '/training?tab=external',
  // An automation run (spec 2026-10-30): its automation's History, the run open.
  [NotificationSubjectType.AUTOMATION_RUN]: (id, item) => {
    const automationId = item.context?.automationId;
    if (typeof automationId !== 'string') return '/tasks/automations';
    const runId = item.context?.runId;
    return typeof runId === 'string'
      ? `/tasks/automations/${automationId}?run=${runId}`
      : `/tasks/automations/${id}`;
  },
  [NotificationSubjectType.COMPANY_FILE]: () => '/files/all',
  [NotificationSubjectType.COMPANY_FOLDER]: (id) => `/files/${id}`,
  [NotificationSubjectType.EMPLOYEE_ASSET]: (id) => `/assets/${id}`,
  [NotificationSubjectType.BENEFIT]: () => '/benefit-plans',
  [NotificationSubjectType.BANK_ACCOUNT]: () => '/profile',
  [NotificationSubjectType.SALARY]: () => '/profile',
  [NotificationSubjectType.BONUS]: () => '/profile',
  [NotificationSubjectType.BRANCH]: (id) => `/branches/${id}`,
  [NotificationSubjectType.COMPANY_ADMIN]: (id) => `/user-access/${id}`,
  [NotificationSubjectType.BULK_IMPORT_JOB]: (id) =>
    `/employees/bulk-imports/${id}`,
};

/** `null` means "render, but don't make it clickable". */
export const resolveNotificationHref = (item: NotificationItem) =>
  resolveHref(item, SUBJECT_ROUTE);
